import { memo, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Grid, MenuItem } from '@mui/material';
import { immediateToast } from 'utils/toast';
import {
    FastField,
    Formik,
    FormikHelpers,
    useFormikContext,
} from 'formik';
import * as Yup from 'yup';

import CitySelector from 'components/formik/city-selector/city-selector';
import { TextField } from 'components/formik/text-field';
import { useDialog } from 'components/layouts/page-with-dialog/dialog-context';
import { useStoreAddressMutation } from '../../api/user.api';
import { handleServerErrors } from 'utils/utils';
import { Address } from '../../types';
import {
    isValidAddress,
    isValidNumbers,
    isValidPhone,
    isValidPostal,
} from 'utils/custom-validation';

import type { AddressFormValues } from '../../types/address.types';
import AddressTypeSelector from '../addresses/address-type-selector';

type AddressFormSource = Partial<Omit<Address, 'city' | 'type'>> & {
    city?: { id?: string | number } | string;
    address_type?: Address['type'];
};

const AddressSchema = Yup.object().shape({
    city_id: Yup.string().required('وارد کردن شهر الزامی است'),

    postal: Yup.string()
        .required('وارد کردن کد پستی الزامی است')
        .length(10, 'فیلد کد پستی باید دقیقاً ۱۰ رقم باشد')
        .matches(isValidNumbers, 'کد پستی فقط باید شامل عدد باشد')
        .matches(isValidPostal, 'لطفاً یک کد پستی معتبر وارد کنید'),

    telephone: Yup.string()
        .required('وارد کردن شماره تلفن ثابت الزامی است')
        .length(
            11,
            'شماره تلفن ثابت باید دقیقاً ۱۱ رقم و شامل کد شهر معتبر باشد',
        )
        .matches(isValidNumbers, 'شماره تلفن ثابت فقط باید شامل عدد باشد')
        .matches(isValidPhone, 'لطفاً یک شماره تلفن معتبر با کد شهر وارد کنید'),

    address: Yup.string()
        .required('وارد کردن آدرس الزامی است')
        .min(3, 'فیلد آدرس باید حداقل ۳ کاراکتر باشد')
        .max(255, 'فیلد آدرس باید حداکثر ۲۵۵ کاراکتر باشد')
        .matches(
            isValidAddress,
            'آدرس فقط می‌تواند شامل حروف و اعداد فارسی، نقطه، ویرگول و خط تیره باشد',
        )
        .test(
            'no-whitespace-only',
            'فیلد آدرس نمی‌تواند فقط شامل فاصله باشد',
            (value) => !value || value.trim().length > 0,
        ),

    address_type: Yup.string().required('انتخاب محل آدرس الزامی است'),

    taxcode: Yup.string().when('address_type', {
        is: 'company',
        then: (schema) =>
            schema
                .required('وارد کردن کد مالیاتی الزامی است')
                .length(10, 'کد مالیاتی باید دقیقاً ۱۰ رقم باشد')
                .matches(isValidNumbers, 'کد مالیاتی فقط باید شامل عدد باشد'),
        otherwise: (schema) => schema.nullable(),
    }),

    refered: Yup.string()
        .min(3, 'فیلد کد معرف باید حداقل ۳ کاراکتر باشد')
        .max(20, 'فیلد کد معرف باید حداکثر ۲۰ کاراکتر باشد'),
});

const NewAddressSchema = AddressSchema.shape({
    taxcode: Yup.string().nullable().notRequired(),
});

const AddressFormTemplate = memo(
    ({
        showTaxCode,
        useCardTypeSelector = false,
        disableAutoFill = false,
        hideTaxCode = false,
    }: {
        showTaxCode?: boolean;
        useCardTypeSelector?: boolean;
        disableAutoFill?: boolean;
        hideTaxCode?: boolean;
    }): JSX.Element => {
        const { values, setFieldValue } = useFormikContext<AddressFormValues>();
        const shouldShowTaxCode =
            !hideTaxCode && (showTaxCode ?? values.address_type === 'company');

        useEffect(() => {
            if (
                (hideTaxCode || values.address_type !== 'company') &&
                values.taxcode
            ) {
                setFieldValue('taxcode', '', false);
            }
        }, [hideTaxCode, setFieldValue, values.address_type, values.taxcode]);

        return (
            <Grid container spacing={2}>
                {useCardTypeSelector && (
                    <Grid item xs={12}>
                        <Alert
                            severity="warning"
                            variant="outlined"
                            sx={{
                                alignItems: 'center',
                                '& .MuiAlert-message': {
                                    color: 'text.primary',
                                    lineHeight: 1.9,
                                },
                            }}
                        >
                            پس از افزودن آدرس، کد مالیاتی مرتبط با آن کدپستی استعلام می‌شود؛ لطفاً در واردکردن اطلاعات دقت کنید.
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    {useCardTypeSelector ? (
                        <AddressTypeSelector
                            value={values.address_type}
                            onChange={(addressType) =>
                                setFieldValue(
                                    'address_type',
                                    addressType,
                                    false,
                                )
                            }
                        />
                    ) : (
                        <FastField
                            component={TextField}
                            name="address_type"
                            label="محل آدرس"
                            select
                            liveValidation={false}
                            icon={
                                <i className="fa-light fa-map-location-dot" />
                            }
                        >
                            <MenuItem value="home">خانه</MenuItem>
                            <MenuItem value="company">محل کار</MenuItem>
                        </FastField>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <FastField
                        icon={<i className="fa-light fa-city" />}
                        component={CitySelector}
                        name="city_id"
                        label="شهر"
                        liveValidation={false}
                        disableAutoFill={disableAutoFill}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FastField
                        name="postal"
                        label="کد پستی"
                        icon={<i className="fa-light fa-mailbox" />}
                        component={TextField}
                        liveValidation={false}
                        disableAutoFill={disableAutoFill}
                        numericOnly
                        ltr="true"
                        maxLength={10}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            ...(disableAutoFill
                                ? { autoComplete: 'new-password' }
                                : {}),
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FastField
                        name="telephone"
                        label="شماره تلفن ثابت"
                        icon={<i className="fa-light fa-phone-rotary" />}
                        component={TextField}
                        liveValidation={false}
                        disableAutoFill={disableAutoFill}
                        numericOnly
                        ltr="true"
                        maxLength={11}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            ...(disableAutoFill
                                ? { autoComplete: 'new-password' }
                                : {}),
                        }}
                    />
                </Grid>

                {shouldShowTaxCode && (
                    <Grid item xs={12} md={6}>
                        <FastField
                            name="taxcode"
                            label="کد مالیاتی"
                            icon={<i className="fa-light fa-id-card-clip" />}
                            component={TextField}
                            liveValidation={false}
                            disableAutoFill={disableAutoFill}
                            ltr="true"
                            maxLength={10}
                            inputProps={
                                disableAutoFill
                                    ? { autoComplete: 'new-password' }
                                    : undefined
                            }
                        />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <FastField
                        name="address"
                        label="نشانی کامل"
                        multiline
                        minRows={3}
                        maxRows={5}
                        maxLength={255}
                        liveValidation={false}
                        icon={<i className="fa-light fa-map-location" />}
                        component={TextField}
                        disableAutoFill={disableAutoFill}
                        inputProps={
                            disableAutoFill
                                ? { autoComplete: 'new-password' }
                                : undefined
                        }
                    />
                </Grid>
            </Grid>
        );
    },
);

const AddressObject = (addressObj?: AddressFormSource): AddressFormValues => ({
    address_type: 'home',
    postal: '',
    telephone: '',
    taxcode: '',
    address: '',
    city_id:
        typeof addressObj?.city === 'object'
            ? String(addressObj?.city?.id ?? '')
            : '',
    ...addressObj,
});

const AddressFormContent = ({
    isDialogOpen,
    isStoring,
}: {
    isDialogOpen: boolean;
    isStoring: boolean;
}) => {
    const { handleSubmit, isSubmitting, resetForm } =
        useFormikContext<AddressFormValues>();

    useEffect(() => {
        if (!isDialogOpen) {
            resetForm();
        }
    }, [isDialogOpen, resetForm]);

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <Box
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    width: 0,
                    height: 0,
                    overflow: 'hidden',
                    opacity: 0,
                    pointerEvents: 'none',
                }}
            >
                <input type="text" name="fake-address-user" autoComplete="username" tabIndex={-1} />
                <input type="password" name="fake-address-password" autoComplete="new-password" tabIndex={-1} />
            </Box>

            <AddressFormTemplate
                useCardTypeSelector
                disableAutoFill
                hideTaxCode
            />

            <Box sx={{ mt: 3 }}>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting || isStoring}
                    fullWidth
                    sx={{ minHeight: 46 }}
                >
                    ذخیره آدرس
                </LoadingButton>
            </Box>
        </form>
    );
};

const AdressesForm = (): JSX.Element => {
    const [storeAddress, { isLoading: isStoring }] = useStoreAddressMutation();
    const { closeDialog, isDialogOpen } = useDialog();
    const isOpen = isDialogOpen('new-address');

    const handleOnSubmit = (
        values: AddressFormValues,
        {
            setStatus,
            setSubmitting,
            resetForm,
        }: FormikHelpers<AddressFormValues>,
    ) => {
        storeAddress({
            ...values,
            taxcode: null,
        })
            .unwrap()
            .then(() => {
                immediateToast('success', {
                    message: 'آدرس جدید ثبت شد',
                    timeout: 2000,
                });

                resetForm();
                closeDialog('new-address');
            })
            .catch((error) => {
                handleServerErrors(error, setStatus);
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Formik<AddressFormValues>
            initialValues={AddressObject({})}
            onSubmit={handleOnSubmit}
            validationSchema={NewAddressSchema}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <AddressFormContent
                isDialogOpen={isOpen}
                isStoring={isStoring}
            />
        </Formik>
    );
};

export { AddressFormTemplate, AddressObject, AddressSchema, AdressesForm };
